/**
 * SizilPro — useServiceMenu
 * TanStack Query hooks for service_items table operations.
 *
 * File: src/modules/proposal-builder/hooks/useServiceMenu.ts
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/store/authStore";
import type { ServiceItem, ServiceItemInsert } from "../types";

const serviceMenuKey = (clientId: string | null, includeInactive: boolean) =>
  ["service-items", clientId ?? "no-client", includeInactive] as const;

function normalizeText(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

function normalizeServiceCategory(
  value: string | null | undefined
): string | null {
  const normalized = normalizeText(value);

  if (!normalized) return null;

  if (
    normalized === "restaurant" ||
    normalized === "restaurants" ||
    normalized === "food service" ||
    normalized === "food-service" ||
    normalized === "hospitality"
  ) {
    return "restaurant";
  }

  if (
    normalized === "commercial" ||
    normalized === "office" ||
    normalized === "offices" ||
    normalized === "retail" ||
    normalized === "janitorial" ||
    normalized === "facility" ||
    normalized === "facilities"
  ) {
    return "commercial";
  }

  if (
    normalized === "construction" ||
    normalized === "post construction" ||
    normalized === "post-construction" ||
    normalized === "postconstruction" ||
    normalized === "renovation" ||
    normalized === "jobsite"
  ) {
    return "construction";
  }

  if (
    normalized === "residential" ||
    normalized === "home" ||
    normalized === "homes" ||
    normalized === "house" ||
    normalized === "houses" ||
    normalized === "housekeeping"
  ) {
    return "residential";
  }

  if (
    normalized === "other" ||
    normalized === "custom" ||
    normalized === "general"
  ) {
    return "other";
  }

  return normalized;
}

function prepareServiceItemPayload(
  item: Partial<ServiceItemInsert>
): Partial<ServiceItemInsert> {
  return {
    ...item,
    name: typeof item.name === "string" ? item.name.trim() : item.name,
    default_frequency:
      typeof item.default_frequency === "string"
        ? item.default_frequency.trim() || null
        : item.default_frequency,
    default_description:
      typeof item.default_description === "string"
        ? item.default_description.trim() || null
        : item.default_description,
    category: normalizeServiceCategory(item.category),
  };
}

// ─── Fetch service items ──────────────────────────────────────
export function useServiceMenu(includeInactive = false) {
  const clientId = useAuthStore((s) => s.clientId);

  return useQuery<ServiceItem[]>({
    queryKey: serviceMenuKey(clientId, includeInactive),
    queryFn: async () => {
      if (!clientId) return [];

      let query = supabase
        .from("service_items")
        .select("*")
        .eq("client_id", clientId)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });

      if (!includeInactive) {
        query = query.eq("active", true);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as ServiceItem[];
    },
    enabled: !!clientId,
    staleTime: 1000 * 60 * 2,
    refetchOnMount: true,
  });
}

// ─── Grouped helper ───────────────────────────────────────────
export function useServiceMenuGrouped() {
  const { data: items = [], ...rest } = useServiceMenu();

  const grouped = items.reduce<Record<string, ServiceItem[]>>((acc, item) => {
    const category = normalizeServiceCategory(item.category) ?? "other";
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {});

  return { grouped, items, ...rest };
}

// ─── Create ───────────────────────────────────────────────────
export function useCreateServiceItem() {
  const clientId = useAuthStore((s) => s.clientId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (item: ServiceItemInsert) => {
      if (!clientId) throw new Error("No client ID");

      const payload = prepareServiceItemPayload(item);

      const { data, error } = await supabase
        .from("service_items")
        .insert({
          ...payload,
          client_id: clientId,
        })
        .select()
        .single();

      if (error) throw error;
      return data as ServiceItem;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service-items", clientId] });
    },
  });
}

// ─── Update ───────────────────────────────────────────────────
export function useUpdateServiceItem() {
  const clientId = useAuthStore((s) => s.clientId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<ServiceItemInsert>;
    }) => {
      if (!clientId) throw new Error("No client ID");

      const payload = prepareServiceItemPayload(updates);

      const { data, error } = await supabase
        .from("service_items")
        .update({
          ...payload,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .eq("client_id", clientId)
        .select()
        .single();

      if (error) throw error;
      return data as ServiceItem;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service-items", clientId] });
    },
  });
}

// ─── Soft delete ──────────────────────────────────────────────
export function useDeactivateServiceItem() {
  const clientId = useAuthStore((s) => s.clientId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!clientId) throw new Error("No client ID");

      const { data, error } = await supabase
        .from("service_items")
        .update({
          active: false,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .eq("client_id", clientId)
        .select()
        .single();

      if (error) throw error;
      return data as ServiceItem;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service-items", clientId] });
    },
  });
}

// ─── Reorder ──────────────────────────────────────────────────
export function useReorderServiceItems() {
  const clientId = useAuthStore((s) => s.clientId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      if (!clientId) throw new Error("No client ID");

      const updates = orderedIds.map((id, index) => ({
        id,
        client_id: clientId,
        sort_order: index,
        updated_at: new Date().toISOString(),
      }));

      const { error } = await supabase
        .from("service_items")
        .upsert(updates, { onConflict: "id" });

      if (error) throw error;
    },
    onMutate: async (orderedIds) => {
      await qc.cancelQueries({ queryKey: serviceMenuKey(clientId, false) });

      const prev = qc.getQueryData<ServiceItem[]>(
        serviceMenuKey(clientId, false)
      );

      if (prev) {
        const reordered = orderedIds
          .map((id, index) => {
            const item = prev.find((s) => s.id === id);
            return item ? { ...item, sort_order: index } : null;
          })
          .filter(Boolean) as ServiceItem[];

        qc.setQueryData(serviceMenuKey(clientId, false), reordered);
      }

      return { prev };
    },
    onError: (_, __, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(serviceMenuKey(clientId, false), ctx.prev);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["service-items", clientId] });
    },
  });
}